import { zodResolver } from '@hookform/resolvers/zod';

import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { Controller,useForm } from 'react-hook-form';

import { markProductionCompleteFormSchema,VehicleStage } from '@ekhodealer/ekho-common';
import { Button, Checkbox, Group, SkeletonRect,Stack, Text, toast } from '@ekhodealer/ekho-common/rev';

import type { ActionItem } from '@ekhodealer/ekho-common';

import { useGetMarkProductionComplete } from '../../../api/progress/useGetMarkProductionComplete';
import { useMarkProductionComplete } from '../../../api/progress/useMarkProductionComplete';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

interface MarkProductionCompleteDetailProps {
	actionItem: ActionItem;
}

interface FormData {
	isConfirmed: boolean;
}

/**
 * MarkProductionCompleteDetail component for marking vehicle production as complete.
 * Displays a checkbox for confirmation and a submit button.
 */
export const MarkProductionCompleteDetail = ({ actionItem }: MarkProductionCompleteDetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);

	const markProductionCompleteMutation = useMarkProductionComplete(orderId);
	const { data: existingData, isLoading: isLoadingData } = useGetMarkProductionComplete(orderId);

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
	} = useForm<FormData>({
		resolver: zodResolver(markProductionCompleteFormSchema),
		defaultValues: {
			isConfirmed: false,
		},
	});

	// Pre-populate form with existing data
	useEffect(() => {
		if (existingData?.vehicleStage === VehicleStage.COMPLETE) {
			reset({ isConfirmed: true });
		}
	}, [existingData, reset]);

	const submitHandler = handleSubmit(async (data: FormData) => {
		if (!data.isConfirmed) {
			return;
		}

		try {
			await markProductionCompleteMutation.mutateAsync();
			toast({
				message: 'Vehicle production marked as complete successfully',
				variant: 'success',
			});
			// Navigate back to progress view on success
			navigateBack();
			// Reset form on success
			reset();
		} catch (err) {
			// apiClient.normalizeError returns a plain ApiError object, not an Error instance
			const errorMessage =
				err instanceof Error
					? err.message
					: (err as { message?: string })?.message ??
						'Failed to mark vehicle production as complete';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		}
	});

	return (
		<Stack itemsSpacing='24'>
			<Stack itemsSpacing='8'>
				<Text size='body' weight='semibold'>
					{actionItem.config.title}
				</Text>
				<Text size='bodySmall' color='secondary'>
					{actionItem.config.instructions ||
						'Confirm that vehicle production is complete. This will update the vehicle stage to COMPLETE.'}
				</Text>
			</Stack>

			<Stack itemsSpacing='16'>
				{/* Show completion status if already complete */}
				{isLoadingData ? (
					<SkeletonRect width='100%' height={48} />
				) : existingData?.vehicleStage === VehicleStage.COMPLETE ? (
					<Text size='body' color='success'>
						✓ Production already complete
					</Text>
				) : null}

				{/* Show form if not complete */}
				{existingData?.vehicleStage !== VehicleStage.COMPLETE && (
					<>
						<Controller
							name='isConfirmed'
							control={control}
							render={({ field: { onChange, value } }) => (
								<Stack itemsSpacing='4'>
									<Group itemsSpacing='8' itemsAlignY='center' itemsAlignX='start'>
										<Checkbox
											isSelected={value}
											onChange={onChange}
											isDisabled={
												isSubmitting || markProductionCompleteMutation.isPending
											}
											ariaLabel='Confirm vehicle production is complete'
										/>
										<Text size='bodySmall'>Confirm</Text>
									</Group>
									{errors.isConfirmed && (
										<Text size='footnote' color='error'>
											{errors.isConfirmed.message}
										</Text>
									)}
								</Stack>
							)}
						/>

						<Button
							size='large'
							isFullWidth
							onPress={() => {
								submitHandler();
							}}
							isLoading={isSubmitting || markProductionCompleteMutation.isPending}
							isDisabled={isLoadingData}>
							{actionItem.config.buttonTitle || 'Mark production as complete'}
						</Button>
					</>
				)}
			</Stack>
		</Stack>
	);
};
